/**
 * Custom hook for fetching match odds
 * Uses React Query with automatic refetching for live odds updates
 */

import { useQuery } from '@tanstack/react-query';
import { API_URL } from '@/utils/constants';

/**
 * Fetch detailed match odds with multiple back/lay prices
 * @param {string} matchId - Market ID
 * @param {Object} matchData - Match data with team names
 * @param {boolean} isMobile - Whether mobile view (affects odds count)
 * @returns {Object} React Query result with transformed odds data
 */
export const useMatchOdds = (matchId, matchData, isMobile = false) => {
  return useQuery({
    queryKey: ['matchOdds', matchId, isMobile],
    queryFn: async () => {
      if (!matchId) return null;

      const response = await fetch(
        `${API_URL}/sports/market-odds?market_id=${matchId}`,
        {
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch odds');
      }

      const data = await response.json();

      if (!data || !Array.isArray(data) || data.length === 0) {
        return null;
      }

      const market = data[0];
      if (!market?.runners) {
        return null;
      }

      // Transform API data to show multiple odds (3 back, 3 lay) with volumes
      const transformed = [];
      const runners = market.runners;

      runners.forEach((runner, index) => {
        let teamName = '';
        let outcome = '';

        if (runners.length === 2) {
          if (index === 0) {
            teamName = matchData?.team1 || '';
            outcome = 'one';
          } else {
            teamName = matchData?.team2 || '';
            outcome = 'two';
          }
        } else if (runners.length === 3) {
          if (index === 0) {
            teamName = matchData?.team1 || '';
            outcome = 'one';
          } else if (index === 1) {
            teamName = 'The Draw';
            outcome = 'x';
          } else {
            teamName = matchData?.team2 || '';
            outcome = 'two';
          }
        }

        // Get all back and lay odds
        // Mobile: show last BACK (position 2) and first LAY (position 0)
        // Desktop: show 3 back and 3 lay (6 total)
        const backOddsCount = 3; // Always fetch 3 for BACK (to show last one on mobile)
        const layOddsCount = isMobile ? 1 : 3; // Mobile: only first LAY, Desktop: all 3

        const backOdds = (runner.ex?.availableToBack || [])
          .slice(0, backOddsCount)
          .map((back) => ({
            value: back.price || '--',
            volume: back.size || 0,
          }));

        const layOdds = (runner.ex?.availableToLay || [])
          .slice(0, layOddsCount)
          .map((lay) => ({
            value: lay.price || '--',
            volume: lay.size || 0,
          }));

        // Pad arrays to ensure correct number of items
        while (backOdds.length < backOddsCount) {
          backOdds.push({ value: '--', volume: 0 });
        }
        while (layOdds.length < layOddsCount) {
          layOdds.push({ value: '--', volume: 0 });
        }

        transformed.push({
          team: teamName,
          outcome: outcome,
          backOdds: backOdds,
          layOdds: layOdds,
        });
      });

      return transformed;
    },
    enabled: !!matchId && !!matchData,
    staleTime: 1 * 1000, // 1 second - odds change frequently
    refetchInterval: 2 * 1000, // Refetch every 2 seconds for live updates
    refetchIntervalInBackground: true, // Continue refetching in background
    retry: 2,
    retryDelay: 1000,
  });
};
